/**
 * W2CreatorModal — Pops up after a paystub is downloaded.
 *
 * Calculation philosophy (updated 2026-02-XX per user request):
 *   The W-2 is now built by ANNUALIZING the source paystub from January
 *   through December, as if the employee earned that same paystub amount
 *   every pay period of the chosen tax year.  We deliberately ignore the
 *   paystub's YTD column because the user wants a full-year W-2 derived
 *   purely from a single paystub's per-period numbers.
 *
 *      annual_value = current_period_value × pay_periods_per_year
 *
 *   where pay_periods_per_year is determined by the selected pay
 *   frequency (weekly=52, bi-weekly=26, semi-monthly=24, monthly=12).
 *
 * IRS rules applied on top of the annualized gross:
 *   • Box 1  (Wages)              = annualized gross
 *   • Box 2  (Federal tax)        = annualized federal
 *   • Box 3  (SS wages)           = min(annualized gross, SS wage base)
 *   • Box 4  (SS tax)             = Box 3 × 6.2%
 *   • Box 5  (Medicare wages)     = annualized gross (no cap)
 *   • Box 6  (Medicare tax)       = Box 5 × 1.45% + max(0, Box 5 - 200k) × 0.9%
 *   • Box 16 (State wages)        = annualized gross
 *   • Box 17 (State tax)          = annualized state
 *   • Box 18 (Local wages)        = annualized gross
 *   • Box 19 (Local tax)          = annualized SDI / disability
 *   • Box 20 (Locality)           = "CASDI" if state=CA, else blank
 *
 * Year selector: current year back through 6 prior years (7 options).
 */
import React, { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, FileText, Download, Loader2, Info } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Social Security wage base by tax year (US — set by SSA each year)
const SS_WAGE_BASE = {
  2020: 137700, 2021: 142800, 2022: 147000, 2023: 160200,
  2024: 168600, 2025: 176100, 2026: 184500,
};
const SS_RATE = 0.062;
const MEDICARE_RATE = 0.0145;
const ADDITIONAL_MEDICARE_RATE = 0.009; // on wages > $200,000

// Number of pay periods per calendar year for each supported pay frequency.
// These are the only values the wizard's `payFrequency` field uses today.
const PERIODS_PER_YEAR = {
  weekly: 52,
  "bi-weekly": 26,
  biweekly: 26,
  "semi-monthly": 24,
  semimonthly: 24,
  monthly: 12,
};

const fmt = (n) => {
  const v = Number(n) || 0;
  return v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const toNum = (v) => {
  const n = parseFloat(String(v || "").replace(/[^0-9.\-]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

// Module-scope helper so React doesn't recreate the component each render.
function W2Row({ label, k, hint, form, update }) {
  return (
    <div className="grid grid-cols-[1fr_140px] items-center gap-2">
      <label className="text-xs text-zinc-300">
        {label}
        {hint && <span className="text-[10px] text-zinc-500 ml-1">({hint})</span>}
      </label>
      <input
        type="text"
        inputMode="decimal"
        value={form[k]}
        onChange={(e) => update(k, e.target.value)}
        className="px-2 py-1 rounded bg-zinc-900 border border-zinc-700 text-white text-xs text-right focus:outline-none focus:ring-1 focus:ring-emerald-500"
        data-testid={`w2-${k}`}
      />
    </div>
  );
}

export default function W2CreatorModal({ open, onClose, formData, initialYear, onFinished, queueInfo, paystubClientId, initialSavedW2, sourceYear }) {
  // Year dropdown options: current year + 6 prior years (newest first).
  const currentYear = new Date().getFullYear();
  const yearOptions = useMemo(
    () => Array.from({ length: 7 }, (_, i) => currentYear - i),
    [currentYear]
  );
  // Default to caller's choice (from the post-paystub prompt) or prior year.
  const defaultYear = initialYear || (currentYear - 1);

  // Pull the most recent paystub for source numbers (current period only).
  const sourcePaystub = useMemo(() => {
    const list = formData?.paystubs || [];
    return list[list.length - 1] || null;
  }, [formData]);

  // Pay periods per year derived from the wizard's pay frequency
  const payFrequency = String(formData?.payFrequency || "bi-weekly").toLowerCase();
  const periodsPerYear = PERIODS_PER_YEAR[payFrequency] || 26;

  // ── Current-period values from the source paystub (NOT ytd) ──
  // Wizard-generated stubs nest values under `current.*`; uploaded
  // paystubs may use flat top-level keys. Support both shapes.
  const cur = sourcePaystub?.current || sourcePaystub || {};
  const curGross = toNum(cur.grossPay);
  const curFederal = toNum(cur.federalTax ?? cur.federal);
  const curState = toNum(cur.stateTax ?? cur.state);
  const curSDI = toNum(cur.sdi);
  const curSS = toNum(cur.socialSecurity ?? cur.social_security ?? cur.ss);
  const curMedicare = toNum(cur.medicare);

  // Annualized projections (Jan → Dec)
  const annualGross = +(curGross * periodsPerYear).toFixed(2);
  const annualFederal = +(curFederal * periodsPerYear).toFixed(2);
  const annualState = +(curState * periodsPerYear).toFixed(2);
  const annualSDI = +(curSDI * periodsPerYear).toFixed(2);
  const annualSS = +(curSS * periodsPerYear).toFixed(2);
  const annualMedicare = +(curMedicare * periodsPerYear).toFixed(2);

  // Auto-calc Box values for the chosen year
  const [taxYear, setTaxYear] = useState(defaultYear);
  // 2026-06-24 — Sync taxYear when the caller passes a new initialYear
  // (e.g. user picks a different year on the W-2/1099 prompt then opens
  // the modal). Without this the modal would keep its previous selection.
  React.useEffect(() => {
    if (initialYear) setTaxYear(initialYear);
  }, [initialYear, open]);
  const wageBase = SS_WAGE_BASE[taxYear] || 168600;
  const box1 = annualGross;
  const box3Auto = Math.min(annualGross, wageBase);
  const box4Auto = annualSS > 0 ? annualSS : +(box3Auto * SS_RATE).toFixed(2);
  const box5Auto = annualGross;
  const box6Auto = annualMedicare > 0
    ? annualMedicare
    : +((box5Auto * MEDICARE_RATE) + Math.max(0, box5Auto - 200000) * ADDITIONAL_MEDICARE_RATE).toFixed(2);

  // User-editable W-2 fields (initialized from paystub + auto-calc)
  const [form, setForm] = useState({
    employee_ssn: formData?.employeeSSN ? `XXX-XX-${String(formData.employeeSSN).slice(-4)}` : "",
    employer_ein: "",                       // ← always manual (not on paystub)
    box15_employer_state_id: "",            // ← always manual
    control_number: "",
    box1_wages: box1,
    box2_federal_tax: annualFederal,
    box3_ss_wages: box3Auto,
    box4_ss_tax: box4Auto,
    box5_medicare_wages: box5Auto,
    box6_medicare_tax: box6Auto,
    // 2026-02-28 (user request) — expose Boxes 16 & 18 (state / local
    // wages) as editable fields so EVERYTHING on the W-2 can be edited
    // before generation.  Default to Box 1 (state/local wages == gross).
    box16_state_wages: box1,
    box17_state_tax: annualState,
    box18_local_wages: box1,
    box19_local_tax: annualSDI,
  });

  // 2026-02-08 — "Add another year" flow. When the caller reopens the
  // modal for an existing saved W-2 (initialSavedW2), we hydrate every
  // editable box from that prior year so the user only tweaks whatever
  // changed for the new year.
  React.useEffect(() => {
    if (!initialSavedW2 || !open) return;
    const p = initialSavedW2.payload || {};
    setForm((prev) => ({
      ...prev,
      employee_ssn: p.employee_ssn || prev.employee_ssn,
      employer_ein: p.employer_ein || prev.employer_ein,
      box15_employer_state_id: p.box15_employer_state_id || prev.box15_employer_state_id,
      control_number: p.control_number || prev.control_number,
      box1_wages: p.box1_wages ?? prev.box1_wages,
      box2_federal_tax: p.box2_federal_tax ?? prev.box2_federal_tax,
      box3_ss_wages: p.box3_ss_wages ?? prev.box3_ss_wages,
      box4_ss_tax: p.box4_ss_tax ?? prev.box4_ss_tax,
      box5_medicare_wages: p.box5_medicare_wages ?? prev.box5_medicare_wages,
      box6_medicare_tax: p.box6_medicare_tax ?? prev.box6_medicare_tax,
      box16_state_wages: p.box16_state_wages ?? p.box1_wages ?? prev.box16_state_wages,
      box17_state_tax: p.box17_state_tax ?? prev.box17_state_tax,
      box18_local_wages: p.box18_local_wages ?? p.box1_wages ?? prev.box18_local_wages,
      box19_local_tax: p.box19_local_tax ?? prev.box19_local_tax,
    }));
  }, [initialSavedW2, open]);

  const [generating, setGenerating] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  // 2026-02 — "Data Source" mode: "paystub" auto-imports numbers from the
  // latest paystub (annualized), "manual" lets the user type every box.
  const hasPaystub = !!sourcePaystub && (curGross > 0);
  const [dataSource, setDataSource] = useState(hasPaystub ? "paystub" : "manual");
  // 2026-02-08 — % bump control for the master-gross recalc.
  const [pctBump, setPctBump] = useState(5);
  // Ratios derived from the source paystub (fed/gross, state/gross,
  // sdi/gross). Used by recalcFromGross so editing Box 1 re-derives every
  // other box at the same effective withholding rate. Falls back to the
  // current form's ratios in manual mode so scaling still works.
  const currentBox1ForRates = toNum(form.box1_wages);
  const rates = useMemo(() => {
    if (annualGross > 0) {
      return {
        federal: annualFederal / annualGross,
        state:   annualState   / annualGross,
        sdi:     annualSDI     / annualGross,
      };
    }
    if (currentBox1ForRates > 0) {
      return {
        federal: toNum(form.box2_federal_tax) / currentBox1ForRates,
        state:   toNum(form.box17_state_tax)  / currentBox1ForRates,
        sdi:     toNum(form.box19_local_tax)  / currentBox1ForRates,
      };
    }
    return { federal: 0, state: 0, sdi: 0 };
  }, [annualGross, annualFederal, annualState, annualSDI, currentBox1ForRates, form.box2_federal_tax, form.box17_state_tax, form.box19_local_tax]);

  const switchToManual = () => {
    setDataSource("manual");
    setForm({
      employee_ssn: form.employee_ssn,
      employer_ein: form.employer_ein,
      box15_employer_state_id: form.box15_employer_state_id,
      control_number: form.control_number,
      box1_wages: 0,
      box2_federal_tax: 0,
      box3_ss_wages: 0,
      box4_ss_tax: 0,
      box5_medicare_wages: 0,
      box6_medicare_tax: 0,
      box16_state_wages: 0,
      box17_state_tax: 0,
      box18_local_wages: 0,
      box19_local_tax: 0,
    });
    toast.info("Switched to manual entry — all boxes cleared");
  };

  const switchToPaystub = () => {
    setDataSource("paystub");
    setForm((p) => ({
      ...p,
      box1_wages: box1,
      box2_federal_tax: annualFederal,
      box3_ss_wages: box3Auto,
      box4_ss_tax: box4Auto,
      box5_medicare_wages: box5Auto,
      box6_medicare_tax: box6Auto,
      box16_state_wages: box1,
      box17_state_tax: annualState,
      box18_local_wages: box1,
      box19_local_tax: annualSDI,
    }));
    toast.success("Imported from your paystub — annualized totals filled in");
  };

  if (!open) return null;

  // 2026-02-28 (user request) — Smart cascading field editor.  The goal
  // is: "I want everything able to be edited and auto-calculated so all
  // numbers match."  Rules:
  //
  //  • Edit Box 1 (gross)         → recalc Box 2/3/4/5/6/16/17/18/19
  //                                 using current ratios + IRS caps.
  //  • Edit Box 3 (SS wages)      → clamp to [0, min(gross, SS cap)],
  //                                 recalc Box 4 = Box 3 × 6.2%.
  //  • Edit Box 5 (Medicare wages)→ recalc Box 6 using the Medicare
  //                                 formula (1.45% + 0.9% over $200k).
  //  • Edit Box 16 (state wages)  → stays independent (overrides Box 1
  //                                 sync in payload build).
  //  • Edit Box 18 (local wages)  → same — overrides Box 1 sync.
  //  • Edit Box 2 / 4 / 6 / 17 / 19 → manual override; just the typed
  //                                 value is stored.  Rates recompute
  //                                 via the useMemo above for the next
  //                                 gross-driven recalc.
  //
  // Partial numeric input (e.g. "50.") is preserved as a string so the
  // decimal doesn't vanish mid-typing.
  const updateField = (k, v) => {
    if (k === "box1_wages") {
      recalcFromGross(v);
      return;
    }
    setForm((p) => {
      const next = { ...p, [k]: v };
      const num = toNum(v);
      if (k === "box3_ss_wages") {
        // Keep Box 3 in the legal range [0, min(gross, SS cap)] and
        // auto-match Box 4 to 6.2% of it.
        const gross = toNum(p.box1_wages);
        const clamped = Math.max(0, Math.min(num, Math.min(gross || Infinity, wageBase)));
        next.box3_ss_wages = v === "" ? "" : clamped;
        next.box4_ss_tax = +(clamped * SS_RATE).toFixed(2);
      } else if (k === "box5_medicare_wages") {
        // Box 6 = 1.45% + 0.9% over $200k.
        const med = num;
        next.box6_medicare_tax = +((med * MEDICARE_RATE) + Math.max(0, med - 200000) * ADDITIONAL_MEDICARE_RATE).toFixed(2);
      }
      return next;
    });
  };

  // Backwards-compat shim: the old `update(k, v)` call-sites continue
  // to work (they all target non-cascading free-form fields).
  const update = updateField;

  // Given a fresh gross figure, roll every other W-2 box off the same
  // ratios + IRS caps. Rounded to two decimals. Called by the master
  // gross input, the ± % bump buttons, and the smart updateField
  // handler when the user edits Box 1 directly.
  const recalcFromGross = (raw) => {
    const gross = toNum(raw);
    const box3 = Math.min(gross, wageBase);
    const box6 = +((gross * MEDICARE_RATE) + Math.max(0, gross - 200000) * ADDITIONAL_MEDICARE_RATE).toFixed(2);
    setForm((p) => ({
      ...p,
      // Preserve exactly what the user is typing (raw) so partial
      // digits like "50" or "50." don't get clobbered mid-typing.
      box1_wages:          raw,
      box2_federal_tax:    +(gross * rates.federal).toFixed(2),
      box3_ss_wages:       box3,
      box4_ss_tax:         +(box3 * SS_RATE).toFixed(2),
      box5_medicare_wages: gross,
      box6_medicare_tax:   box6,
      box16_state_wages:   gross,
      box17_state_tax:     +(gross * rates.state).toFixed(2),
      box18_local_wages:   gross,
      box19_local_tax:     +(gross * rates.sdi).toFixed(2),
    }));
  };

  const bumpMasterByPercent = (pct) => {
    const cur = toNum(form.box1_wages);
    if (cur <= 0 || !Number.isFinite(pct)) {
      toast.error("Enter a base Gross Wages first");
      return;
    }
    const next = +(cur * (1 + pct / 100)).toFixed(2);
    recalcFromGross(next);
    toast.success(`${pct >= 0 ? 'Increased' : 'Reduced'} every box by ${Math.abs(pct)}%`);
  };

  const onYearChange = (y) => {
    setTaxYear(y);
    // Carried-forward / manual W-2s keep their numbers exactly when the year changes.
    if (dataSource !== "paystub" || !hasPaystub) return;
    // Year only affects the SS wage cap on Box 3/4. Other annualized
    // values stay the same — the user can still tweak any field.
    const newBase = SS_WAGE_BASE[y] || 168600;
    const newBox3 = Math.min(annualGross, newBase);
    setForm((p) => ({
      ...p,
      box3_ss_wages: newBox3,
      box4_ss_tax: annualSS > 0 ? annualSS : +(newBox3 * SS_RATE).toFixed(2),
    }));
  };

  // Recompute Boxes 3-6 from Box 1 using the selected year's SS cap + rates.
  const recalcSsMedicare = () => {
    const gross = toNum(form.box1_wages);
    const box3 = Math.min(gross, wageBase);
    setForm((p) => ({
      ...p,
      box3_ss_wages: box3,
      box4_ss_tax: +(box3 * SS_RATE).toFixed(2),
      box5_medicare_wages: gross,
      box6_medicare_tax: +((gross * MEDICARE_RATE) + Math.max(0, gross - 200000) * ADDITIONAL_MEDICARE_RATE).toFixed(2),
    }));
    toast.success(`Social Security & Medicare recalculated for ${taxYear}`);
  };

  // Boxes the editor doesn't expose (7-14, 20) carry over from a seeded W-2.
  const seedPayload = initialSavedW2?.payload || {};
  const carryExtras = Object.fromEntries(
    Object.entries(seedPayload).filter(([k]) => /^box(7|8|10|11|12[a-d]|13|14)_/.test(k))
  );

  // Build the API payload from the current form state. Shared between
  // Preview and Generate so both render the exact same PDF.
  const buildPayload = () => ({
    ...carryExtras,
    tax_year: taxYear,
    employee_ssn: form.employee_ssn || "",
    employer_ein: form.employer_ein,
    employer_name: formData.companyName || "",
    employer_address1: formData.companyAddress1 || "",
    employer_address2: formData.companyAddress2 || null,
    employer_city: formData.companyCity || "",
    employer_state: formData.companyState || "",
    employer_zip: formData.companyZip || "",
    control_number: form.control_number || null,
    employee_name: formData.employeeName || "",
    employee_address1: formData.employeeAddress1 || "",
    employee_address2: formData.employeeAddress2 || null,
    employee_city: formData.employeeCity || "",
    employee_state: formData.employeeState || formData.companyState || "",
    employee_zip: formData.employeeZip || "",
    box1_wages: toNum(form.box1_wages),
    box2_federal_tax: toNum(form.box2_federal_tax),
    box3_ss_wages: toNum(form.box3_ss_wages),
    box4_ss_tax: toNum(form.box4_ss_tax),
    box5_medicare_wages: toNum(form.box5_medicare_wages),
    box6_medicare_tax: toNum(form.box6_medicare_tax),
    box15_state: formData.companyState || seedPayload.box15_state || "CA",
    box15_employer_state_id: form.box15_employer_state_id,
    // 2026-02-28 (user request) — Boxes 16 & 18 are editable; fall back
    // to Box 1 when the user leaves them untouched so a stock paystub
    // still produces the canonical "state wages = gross" shape.
    box16_state_wages: toNum(form.box16_state_wages) || toNum(form.box1_wages),
    box17_state_tax: toNum(form.box17_state_tax),
    box18_local_wages: toNum(form.box18_local_wages) || toNum(form.box1_wages),
    box19_local_tax: toNum(form.box19_local_tax),
    box20_locality_name: seedPayload.box20_locality_name ?? ((formData.companyState || "").toUpperCase() === "CA" ? "CASDI" : ""),
  });

  const previewW2 = async () => {
    if (!form.employer_ein) { toast.error("Enter Employer Identification Number (EIN)"); return; }
    if (!form.box15_employer_state_id) { toast.error("Enter Employer's state ID number"); return; }
    setPreviewing(true);
    try {
      const res = await axios.post(`${API}/api/w2/generate-pdf`, buildPayload(), { responseType: "blob" });
      // Revoke any previous preview URL to avoid memory leak
      if (previewUrl) window.URL.revokeObjectURL(previewUrl);
      const url = window.URL.createObjectURL(res.data);
      setPreviewUrl(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to preview W-2");
    } finally {
      setPreviewing(false);
    }
  };

  const generate = async () => {
    if (!form.employer_ein) { toast.error("Enter Employer Identification Number (EIN)"); return; }
    if (!form.box15_employer_state_id) { toast.error("Enter Employer's state ID number"); return; }
    setGenerating(true);
    try {
      const payload = buildPayload();
      const res = await axios.post(`${API}/api/w2/generate-pdf`, payload, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const safeName = (formData.employeeName || "Employee").replace(/[^a-z0-9_-]+/gi, "_");
      a.download = `W-2_${taxYear}_${safeName}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`W-2 ${taxYear} downloaded`);

      // 2026-02-08 — persist so the user can reopen and "Add another year"
      // pre-filled from this one. Best-effort: failures never block the UX.
      try {
        await axios.post(`${API}/api/w2-saved`, {
          paystub_client_id: paystubClientId || null,
          employee_name: formData.employeeName || "",
          employer_name: formData.companyName || "",
          tax_year: taxYear,
          payload,
        });
      } catch (saveErr) {
        console.error("Failed to persist saved W-2:", saveErr);
      }

      // Multi-year queue: if a parent-supplied onFinished exists, call
      // it so the wizard can hop to the next year.  Otherwise just
      // close as before.
      if (typeof onFinished === "function") {
        onFinished();
      } else {
        onClose();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate W-2");
    } finally {
      setGenerating(false);
    }
  };

  // Human-readable label for the frequency used in the helper banner
  const freqLabel = ({
    weekly: "weekly (52 pay periods)",
    "bi-weekly": "bi-weekly (26 pay periods)",
    biweekly: "bi-weekly (26 pay periods)",
    "semi-monthly": "semi-monthly (24 pay periods)",
    semimonthly: "semi-monthly (24 pay periods)",
    monthly: "monthly (12 pay periods)",
  })[payFrequency] || `${periodsPerYear} pay periods`;

  return (
    <div className="fixed inset-0 z-[70] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4" data-testid="w2-modal">
      <div className="bg-zinc-950 border border-zinc-700 rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 z-10 bg-zinc-950 border-b border-zinc-800 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/15 border border-emerald-500/40">
              <FileText className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                W-2 Wage and Tax Statement
                {queueInfo && (
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-400/40" data-testid="w2-queue-progress">
                    Year {queueInfo.index + 1} of {queueInfo.total}
                  </span>
                )}
              </h2>
              <p className="text-[11px] text-zinc-500">
                {sourceYear
                  ? `Carried forward from ${sourceYear} — every box copied exactly. Edit anything for ${taxYear}.`
                  : queueInfo
                  ? `Numbers pre-filled from the paystub — edit any box below for ${taxYear} before downloading.`
                  : hasPaystub
                  ? "Annualized January → December from this paystub\u2019s per-period amounts"
                  : "Type each box — the math auto-matches as you go"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-zinc-400 hover:text-white p-1 rounded-lg hover:bg-zinc-800"
            data-testid="w2-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* 2026-02 — Data source chooser */}
          <div className="rounded-lg border border-zinc-700 bg-zinc-900/60 p-3" data-testid="w2-source-panel">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold">Where should the numbers come from?</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={switchToPaystub}
                disabled={!hasPaystub}
                className={`flex-1 min-w-[220px] text-left px-3 py-2.5 rounded-lg border-2 transition-colors ${
                  dataSource === "paystub"
                    ? 'border-emerald-500 bg-emerald-500/15'
                    : hasPaystub
                      ? 'border-zinc-700 bg-zinc-900 hover:border-zinc-600'
                      : 'border-zinc-800 bg-zinc-900/40 opacity-50 cursor-not-allowed'
                }`}
                data-testid="w2-source-paystub"
              >
                <div className={`text-xs font-bold ${dataSource === "paystub" ? 'text-emerald-200' : 'text-white'}`}>
                  📄 Import from Paystub {dataSource === "paystub" && '✓'}
                </div>
                <div className="text-[10px] text-zinc-400 mt-0.5">
                  {hasPaystub
                    ? `Annualize latest paystub × ${periodsPerYear} periods`
                    : "No paystub data available — fill in Paystub step first"}
                </div>
              </button>
              <button
                type="button"
                onClick={switchToManual}
                className={`flex-1 min-w-[220px] text-left px-3 py-2.5 rounded-lg border-2 transition-colors ${
                  dataSource === "manual"
                    ? 'border-amber-500 bg-amber-500/15'
                    : 'border-zinc-700 bg-zinc-900 hover:border-zinc-600'
                }`}
                data-testid="w2-source-manual"
              >
                <div className={`text-xs font-bold ${dataSource === "manual" ? 'text-amber-200' : 'text-white'}`}>
                  ✏️ Manual Entry {dataSource === "manual" && '✓'}
                </div>
                <div className="text-[10px] text-zinc-400 mt-0.5">
                  Type every box yourself — clears the current values
                </div>
              </button>
            </div>
          </div>

          {/* Tax year + EIN + State ID */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-zinc-400 mb-1">Tax year</label>
              <select
                value={taxYear}
                onChange={(e) => onYearChange(Number(e.target.value))}
                className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-white text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
                data-testid="w2-year"
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-[10px] uppercase tracking-wider text-zinc-400 mb-1">
                Employer Identification Number (EIN) <span className="text-amber-400">*required</span>
              </label>
              <input
                type="text"
                value={form.employer_ein}
                onChange={(e) => update("employer_ein", e.target.value)}
                placeholder="46-4584485"
                className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-white text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
                data-testid="w2-ein"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-zinc-400 mb-1">
                Employer&apos;s state ID number <span className="text-amber-400">*required</span>
              </label>
              <input
                type="text"
                value={form.box15_employer_state_id}
                onChange={(e) => update("box15_employer_state_id", e.target.value)}
                placeholder="023-5689-1"
                className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-white text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
                data-testid="w2-state-id"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-zinc-400 mb-1">Control number (optional)</label>
              <input
                type="text"
                value={form.control_number}
                onChange={(e) => update("control_number", e.target.value)}
                placeholder="0179045"
                className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-white text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
                data-testid="w2-ctrl"
              />
            </div>
          </div>

          {/* Auto-calc box values (editable) */}
          <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
            <div className="flex items-start gap-2 mb-3">
              <Info className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
              {hasPaystub ? (
              <p className="text-xs text-zinc-400">
                These numbers are projected <span className="text-emerald-300 font-semibold">January through December {taxYear}</span> based
                on the paystub&apos;s per-period amounts paid <span className="text-emerald-300 font-semibold">{freqLabel}</span>.
                Per-period gross <span className="text-emerald-300 font-mono">${fmt(curGross)}</span> × <span className="text-emerald-300 font-mono">{periodsPerYear}</span> = annual <span className="text-emerald-300 font-mono">${fmt(annualGross)}</span>.
                Override any value before generating.
              </p>
              ) : (
              <p className="text-xs text-zinc-400" data-testid="w2-manual-note">
                {sourceYear ? `Values copied from the ${sourceYear} W-2.` : "Manual entry."} Use the % buttons for a raise, or recalculate Social Security / Medicare for {taxYear}.
              </p>
              )}
            </div>

            {/* 2026-02-08 — "Edit one master number and recalc everything" per
                user request. Changing Gross Wages (or bumping by a percent)
                re-derives Boxes 2/3/4/5/6/17/19 using the same paystub
                ratios (fed/gross, state/gross, sdi/gross) so the user
                only touches ONE number. Falls back to standard IRS SS
                cap + 6.2% / Medicare 1.45%+0.9% when a paystub isn't
                available. */}
            <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-3 mb-4" data-testid="w2-master-recalc">
              <div className="flex items-center justify-between mb-2">
                <label className="text-[11px] uppercase tracking-wider text-emerald-300 font-bold">
                  Master Gross Wages (edit → recalculates every box)
                </label>
                <span className="text-[10px] text-emerald-200/60">Box 1 · one number drives all</span>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  inputMode="decimal"
                  value={form.box1_wages}
                  onChange={(e) => recalcFromGross(e.target.value)}
                  className="flex-1 px-3 py-2 rounded-lg bg-zinc-950 border border-emerald-500/50 text-emerald-100 text-lg font-mono text-right focus:outline-none focus:ring-2 focus:ring-emerald-400"
                  data-testid="w2-master-gross"
                />
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => bumpMasterByPercent(-Number(pctBump))}
                    className="px-2 py-2 rounded-md bg-zinc-900 border border-zinc-700 hover:border-red-500 hover:bg-red-500/10 text-red-300 text-xs font-bold"
                    title={`Reduce every box by ${pctBump}%`}
                    data-testid="w2-bump-down"
                  >
                    −{pctBump}%
                  </button>
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    value={pctBump}
                    onChange={(e) => setPctBump(e.target.value)}
                    className="w-14 px-2 py-2 rounded-md bg-zinc-950 border border-zinc-700 text-white text-xs text-center focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    data-testid="w2-bump-pct"
                    aria-label="Bump percent"
                  />
                  <button
                    type="button"
                    onClick={() => bumpMasterByPercent(Number(pctBump))}
                    className="px-2 py-2 rounded-md bg-zinc-900 border border-zinc-700 hover:border-emerald-500 hover:bg-emerald-500/10 text-emerald-300 text-xs font-bold"
                    title={`Increase every box by ${pctBump}%`}
                    data-testid="w2-bump-up"
                  >
                    +{pctBump}%
                  </button>
                </div>
                <button
                  type="button"
                  onClick={recalcSsMedicare}
                  className="px-3 py-2 rounded-md bg-zinc-900 border border-sky-500/50 hover:bg-sky-500/10 text-sky-300 text-xs font-semibold whitespace-nowrap"
                  title={`Recalculate Boxes 3-6 using ${taxYear} rates and wage cap`}
                  data-testid="w2-recalc-ss-medicare"
                >
                  Recalc SS/Medicare ({taxYear})
                </button>
                <button
                  type="button"
                  onClick={() => recalcFromGross(box1)}
                  disabled={!hasPaystub}
                  className="px-3 py-2 rounded-md bg-zinc-900 border border-zinc-700 hover:border-emerald-500 hover:text-emerald-300 text-white text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                  title="Reset gross back to the annualized paystub amount"
                  data-testid="w2-reset-gross"
                >
                  Reset
                </button>
              </div>
              <p className="text-[10px] text-emerald-200/60 mt-1.5">
                Ratios used: fed <span className="font-mono">{(rates.federal * 100).toFixed(2)}%</span>
                {" · "}state <span className="font-mono">{(rates.state * 100).toFixed(2)}%</span>
                {" · "}SDI <span className="font-mono">{(rates.sdi * 100).toFixed(2)}%</span>
                {" · "}SS cap <span className="font-mono">${fmt(wageBase)}</span>
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
              <W2Row form={form} update={update} label="Box 1 — Wages, tips, other comp" k="box1_wages" hint="Jan-Dec annual gross" />
              <W2Row form={form} update={update} label="Box 2 — Federal income tax" k="box2_federal_tax" hint="Jan-Dec federal" />
              <W2Row form={form} update={update} label="Box 3 — Social security wages" k="box3_ss_wages" hint={`cap $${fmt(wageBase)} · drives Box 4`} />
              <W2Row form={form} update={update} label="Box 4 — Social security tax" k="box4_ss_tax" hint="6.2% of Box 3" />
              <W2Row form={form} update={update} label="Box 5 — Medicare wages" k="box5_medicare_wages" hint="no cap · drives Box 6" />
              <W2Row form={form} update={update} label="Box 6 — Medicare tax" k="box6_medicare_tax" hint="1.45% + 0.9% over 200k" />
              <W2Row form={form} update={update} label="Box 16 — State wages" k="box16_state_wages" hint="defaults to Box 1" />
              <W2Row form={form} update={update} label="Box 17 — State income tax" k="box17_state_tax" hint="Jan-Dec state" />
              <W2Row form={form} update={update} label="Box 18 — Local wages" k="box18_local_wages" hint="defaults to Box 1" />
              <W2Row form={form} update={update} label="Box 19 — Local income tax" k="box19_local_tax" hint="Jan-Dec SDI / CASDI" />
            </div>
            {/* 2026-02-28 (user request) — tiny guard-rail that explains
                which boxes cascade when the user edits them, so the
                auto-calc behavior doesn't feel like a hidden surprise. */}
            <p className="text-[10px] text-zinc-500 mt-3 leading-relaxed">
              <span className="text-emerald-400 font-semibold">Auto-match rules:</span>{" "}
              Editing <span className="font-mono text-zinc-300">Box 1</span> re-derives every tax box using your paystub ratios.
              Editing <span className="font-mono text-zinc-300">Box 3</span> auto-updates Box 4 (6.2%).
              Editing <span className="font-mono text-zinc-300">Box 5</span> auto-updates Box 6 (Medicare formula).
              All other boxes are free-form so you can set state / federal to the exact dollar figure you want.
            </p>
          </div>

          {/* Inline PDF preview pane — appears after clicking Preview W-2.
              Editing any field doesn't auto-refresh; user clicks Preview
              again to re-render. Keeps things explicit and fast. */}
          {previewUrl && (
            <div className="rounded-xl border border-emerald-500/40 bg-zinc-900/60 p-2">
              <div className="flex items-center justify-between mb-2 px-1">
                <p className="text-xs font-bold text-emerald-300 uppercase tracking-wider">Preview · click Preview again after edits</p>
                <button
                  onClick={() => { if (previewUrl) window.URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }}
                  className="text-zinc-400 hover:text-white text-xs"
                  data-testid="w2-close-preview"
                >
                  Close preview
                </button>
              </div>
              <iframe
                src={previewUrl}
                title="W-2 preview"
                className="w-full h-[520px] bg-white rounded border border-zinc-700"
                data-testid="w2-preview-frame"
              />
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg bg-zinc-900 border border-zinc-700 hover:bg-zinc-800 text-zinc-200 text-sm font-medium"
            >
              Cancel
            </button>
            <button
              onClick={previewW2}
              disabled={previewing || generating}
              className="px-4 py-2 rounded-lg bg-zinc-800 border border-emerald-500/40 hover:bg-zinc-700 disabled:opacity-60 text-emerald-300 text-sm font-semibold flex items-center gap-2"
              data-testid="w2-preview"
            >
              {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
              Preview W-2
            </button>
            <button
              onClick={generate}
              disabled={generating || previewing}
              className="px-5 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-60 text-white text-sm font-semibold flex items-center gap-2"
              data-testid="w2-generate"
            >
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Generate &amp; Download W-2 ({taxYear})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
